/**
 * Keyboard shortcuts overlay.
 *
 * Shift+? opens; Esc closes. Documents the shortcuts available across
 * the admin dashboard.
 *
 * The overlay is rendered inside ProtectedLayout so it's only active
 * once a user is signed in (no point listing internal shortcuts on the
 * /signin route).
 */
import { useEffect, useState, type JSX } from 'react';
import { Keyboard, X } from 'lucide-react';

interface Shortcut {
  combo: string[];
  label: string;
}

interface ShortcutGroup {
  title: string;
  items: Shortcut[];
}

const GROUPS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    items: [
      { combo: ['/'],            label: 'Focus global search' },
      { combo: ['Esc'],          label: 'Close search / drawer / dialog' },
      { combo: ['Shift', '?'],   label: 'Toggle this shortcuts overlay' },
    ],
  },
  {
    title: 'Tables',
    items: [
      { combo: ['Click', 'header'], label: 'Sort by column' },
      { combo: ['Click', 'row'],    label: 'Open detail drawer' },
    ],
  },
  {
    title: 'Drawers',
    items: [
      { combo: ['Esc'],         label: 'Close drawer' },
      { combo: ['Click', 'backdrop'], label: 'Close drawer' },
    ],
  },
  {
    title: 'TOTP / sign-in',
    items: [
      { combo: ['←', '→'],      label: 'Move between digit boxes' },
      { combo: ['Backspace'],   label: 'Step back to the previous digit' },
      { combo: ['Cmd', 'V'],    label: 'Paste the full 6-digit code' },
    ],
  },
];

export function ShortcutsOverlay(): JSX.Element | null {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Skip when typing in an input/textarea/contenteditable.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const editable = target?.isContentEditable
                    || tag === 'input'
                    || tag === 'textarea'
                    || tag === 'select';

      if (e.key === '?' && e.shiftKey && !editable) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={() => setOpen(false)}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(11, 15, 25, 0.6)',
          backdropFilter: 'blur(4px)',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: 'min(560px, 100%)',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: 20,
          borderRadius: 16,
          background: 'var(--bg-raised)',
          border: '1px solid var(--border)',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              flex: 'none',
              width: 32, height: 32, borderRadius: 10,
              background: 'rgba(99, 102, 241, 0.15)',
              border: '1px solid rgba(99, 102, 241, 0.35)',
              color: 'var(--accent-text)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Keyboard width={16} height={16} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Keyboard shortcuts</h2>
            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Shift+? toggles this panel anywhere in the app.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none',
              padding: 6, borderRadius: 8,
              color: 'var(--text-muted)', cursor: 'pointer',
            }}
          >
            <X width={16} height={16} />
          </button>
        </header>

        {GROUPS.map((g) => (
          <section key={g.title}>
            <h3
              style={{
                margin: 0, marginBottom: 8,
                fontSize: '0.6875rem', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.08em',
                color: 'var(--text-muted)',
              }}
            >
              {g.title}
            </h3>
            <ul
              style={{
                listStyle: 'none', padding: 0, margin: 0,
                display: 'flex', flexDirection: 'column', gap: 6,
              }}
            >
              {g.items.map((s) => (
                <li
                  key={s.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: 'var(--bg-overlay)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', flex: 1 }}>
                    {s.label}
                  </span>
                  <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {s.combo.map((k, i) => (
                      <Kbd key={i} label={k} />
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function Kbd({ label }: { label: string }): JSX.Element {
  return (
    <kbd
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        padding: '2px 7px',
        borderRadius: 6,
        background: 'var(--bg-raised)',
        border: '1px solid var(--border)',
        boxShadow: '0 1px 0 var(--border)',
        fontSize: '0.6875rem',
        fontWeight: 600,
        fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
        color: 'var(--text-primary)',
      }}
    >
      {label}
    </kbd>
  );
}
